import React, { useState } from 'react';
import { FiPlay, FiRefreshCw, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { runIvansJobAndWait, parseResultJson, formatDate, type PerAgencyCheckResult, type JobStatus } from '../ivansMonitorService';

/** Native <input type="date"> gives yyyy-MM-dd; checkAllAgencies.ts wants MM/DD/YYYY. */
function toMMDDYYYY(isoDate: string): string | undefined {
  if (!isoDate) return undefined;
  const [y, m, d] = isoDate.split('-');
  return `${m}/${d}/${y}`;
}

const CheckNowTab: React.FC = () => {
  const [dryRun, setDryRun] = useState(true);
  // Narrows the Inbox search window for every agency in this run — blank
  // uses the portal's own default (~last day), matching checkAllAgencies.ts.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState('');
  const [results, setResults] = useState<PerAgencyCheckResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setResults(null);
    setLog('');
    try {
      const final: JobStatus = await runIvansJobAndWait(
        'ivans-check-all',
        [JSON.stringify({ dryRun, startDate: toMMDDYYYY(startDate), endDate: toMMDDYYYY(endDate) })],
        (status) => {
          setLog(status.log);
        },
      );
      if (final.status !== 'completed') {
        setError('Check Now failed — see log below.');
        return;
      }
      const parsed = parseResultJson<{ perAgency: PerAgencyCheckResult[] }>(final.log);
      setResults(parsed?.perAgency ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Check Now failed unexpectedly.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="ivm-section">
      <div className="ivm-section__header">
        <div className="ivm-section__title">Check Now</div>
      </div>

      <label className="ivm-checkbox-row">
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
        Dry run (find files delayed in IVANS or not received at NowCerts, but don't send an alert email or mark anything as alerted)
      </label>

      <div className="ca-run__grid" style={{ marginBottom: 'var(--space-3)' }}>
        <label className="ca-run__field">
          <span>From (optional — defaults to the portal's own window)</span>
          <input className="ca-run__input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="ca-run__field">
          <span>To (optional)</span>
          <input className="ca-run__input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>

      <button type="button" className="ca-run__btn" disabled={running} onClick={handleRun}>
        {running ? <FiRefreshCw size={13} className="spin" /> : <FiPlay size={13} />}
        {running ? 'Checking every agency…' : 'Check Now'}
      </button>

      {running && (
        <div className="ca-run__status ca-run__status--progress" style={{ marginTop: 'var(--space-3)' }}>
          {log.trim().split('\n').filter(Boolean).pop() ?? 'Starting…'}
        </div>
      )}

      {error && <div className="ca-run__status ca-run__status--error" style={{ marginTop: 'var(--space-3)' }}>{error}</div>}

      {results && results.length === 0 && <div className="ca-empty" style={{ marginTop: 'var(--space-3)' }}>No agencies configured — add one on the Agencies tab first.</div>}

      {results && results.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          {results.map((r) => (
            <div className="ivm-agency-check" key={r.accountCode}>
              <div className="ivm-agency-check__header">
                <strong className="ivm-agency-check__name">{r.agencyName} ({r.accountCode})</strong>
                {r.error ? (
                  <span className="ivm-pill ivm-pill--stuck"><FiAlertTriangle size={11} /> Failed</span>
                ) : r.stuck.length > 0 || r.unconfirmed.length > 0 ? (
                  <span className="ivm-pill ivm-pill--stuck">
                    <FiAlertTriangle size={11} />
                    {[
                      r.stuck.length > 0 ? `${r.stuck.length} delayed in IVANS` : null,
                      r.unconfirmed.length > 0 ? `${r.unconfirmed.length} not received at NowCerts` : null,
                    ].filter(Boolean).join(' · ')}
                  </span>
                ) : (
                  <span className="ivm-pill ivm-pill--clean"><FiCheckCircle size={11} /> Clean</span>
                )}
              </div>
              {r.error ? (
                <div className="ivm-agency-check__error">{r.error}</div>
              ) : (
                <div className="ivm-stat-row">
                  <span>{r.totalCount} file(s) in Inbox (IVANS)</span>
                  <span>{r.receivedCount} Received (IVANS)</span>
                  <span>
                    {r.nowcertsChecked
                      ? `${r.receivedCount - r.unconfirmed.length} of ${r.receivedCount} confirmed in NowCerts`
                      : 'NowCerts not configured for this agency'}
                  </span>
                </div>
              )}
              {(r.stuck.length > 0 || r.unconfirmed.length > 0) && (
                <table className="ca-run__table">
                  <thead>
                    <tr><th>Type</th><th>Carrier</th><th>File</th><th>Date</th><th>Detail</th></tr>
                  </thead>
                  <tbody>
                    {r.stuck.map((f) => (
                      <tr key={f.fileId}>
                        <td>Delayed in IVANS</td>
                        <td>{f.carrier || '(unknown)'}</td>
                        <td>{f.fileId.split('|')[0]}</td>
                        <td>{formatDate(f.deliveredAt)}</td>
                        <td>{f.hoursStuck.toFixed(1)}h{f.isReescalation ? ' (re-escalation)' : ''} — never marked Received by IVANS</td>
                      </tr>
                    ))}
                    {r.unconfirmed.map((f) => (
                      <tr key={f.fileId}>
                        <td>Not Received at NowCerts</td>
                        <td>{f.carrier || '(unknown)'}</td>
                        <td>{f.fileId.split('|')[0]}</td>
                        <td>{formatDate(f.statusDate)}</td>
                        <td>Received by IVANS, but never shows up as ingested in NowCerts</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CheckNowTab;
